import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  inject,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  NgDialogModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { RestrictAccess } from '@principle-theorem/principle-core';
import {
  ICustomReport,
  IRestrictAccessRole,
  IRestrictAccessStaffer,
  RestrictAccessEntity,
} from '@principle-theorem/principle-core/interfaces';
import {
  WithRef,
  all$,
  multiMap,
  multiUndeleted,
} from '@principle-theorem/shared';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

export interface ICustomReportForm {
  name: string;
  description: string;
  restrictAccessTo: RestrictAccessEntity[];
}

export interface ICustomReportDialogRequest {
  customReport?: WithRef<ICustomReport>;
  isPublishing?: boolean;
}

export interface ICustomReportDialogResponse {
  formData?: ICustomReportForm;
  deleteCustomForm?: boolean;
  changeTemplate?: boolean;
}

@Component({
  selector: 'pr-custom-report-dialog',
  templateUrl: './custom-report-dialog.component.html',
  styleUrl: './custom-report-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgDialogModule],
})
export class CustomReportDialogComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  dialogRef = inject<MatDialogRef<CustomReportDialogComponent>>(MatDialogRef);
  data = inject<ICustomReportDialogRequest>(MAT_DIALOG_DATA);

  roles$: Observable<IRestrictAccessRole[]>;
  staff$: Observable<IRestrictAccessStaffer[]>;
  customReport$ = new BehaviorSubject<WithRef<ICustomReport> | undefined>(
    undefined
  );
  form = new TypedFormGroup<ICustomReportForm>({
    name: new TypedFormControl<string>('', [Validators.required]),
    description: new TypedFormControl<string>(''),
    restrictAccessTo: new TypedFormControl<RestrictAccessEntity[]>([]),
  });

  constructor(private _organisation: OrganisationService) {
    if (this.data.customReport) {
      this.customReport$.next(this.data.customReport);
      this.form.patchValue(this.data.customReport, { emitEvent: false });
    }

    this.staff$ = this._organisation.staff$.pipe(
      multiMap((staffer) => RestrictAccess.toStafferAccess(staffer))
    );

    this.roles$ = this._organisation.roleCol$.pipe(
      switchMap((col) => (col ? all$(col).pipe(multiUndeleted()) : of([]))),
      multiMap((role) => RestrictAccess.toRoleAccess(role))
    );
  }

  compareAccessFn(a: RestrictAccessEntity, b: RestrictAccessEntity): boolean {
    return a && b ? a.ref.path === b.ref.path : false;
  }

  getRestrictAccessTo(formData: ICustomReportForm): RestrictAccessEntity[] {
    return formData.restrictAccessTo.length ? formData.restrictAccessTo : [];
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  delete(): void {
    this.dialogRef.close({
      deleteCustomForm: true,
    });
  }

  changeTemplate(): void {
    this.dialogRef.close({
      changeTemplate: true,
    });
  }

  submit(): void {
    if (!this.form.valid) {
      return;
    }
    this.dialogRef.close({
      formData: this.form.value,
      deleteCustomForm: false,
    });
  }
}
